import React from "react";
import {
  FaLock,
  FaShieldAlt,
  FaUserSecret,
  FaAsterisk,
  FaCheckCircle,
} from "react-icons/fa";
import "../styles/ServicePage.css";

// Constants
const CYBERSECURITY_SERVICES = [
  {
    id: 1,
    title: "Security Audits",
    icon: FaShieldAlt,
    description:
      "Comprehensive assessment of your security infrastructure and vulnerabilities.",
    features: [
      "Network Security Assessment",
      "Application Security Review",
      "Infrastructure Evaluation",
      "Policy & Procedure Review",
      "Employee Security Training Assessment",
      "Detailed Audit Report",
    ],
    benefits: [
      "Identify vulnerabilities early",
      "Compliance assurance",
      "Risk mitigation",
      "Operational efficiency",
      "Cost savings",
      "Peace of mind",
    ],
  },
  {
    id: 2,
    title: "Penetration Testing",
    icon: FaUserSecret,
    description:
      "Ethical hacking to simulate real-world attacks and find security gaps.",
    features: [
      "External Penetration Testing",
      "Internal Penetration Testing",
      "Web Application Testing",
      "Social Engineering Tests",
      "Detailed Findings Report",
      "Remediation Recommendations",
    ],
    benefits: [
      "Real-world threat simulation",
      "Actionable insights",
      "Priority-based fixes",
      "Reduced breach risk",
      "Improved security posture",
      "Compliance requirements",
    ],
  },
  {
    id: 3,
    title: "Data Protection",
    icon: FaLock,
    description:
      "Protect sensitive data with encryption and secure data handling practices.",
    features: [
      "Data Encryption",
      "Access Control Management",
      "Data Backup & Recovery",
      "Secure Data Deletion",
      "Data Loss Prevention",
      "Compliance Management (GDPR, CCPA)",
    ],
    benefits: [
      "Legal compliance",
      "Customer trust",
      "Avoid penalties",
      "Secure transactions",
      "Business continuity",
      "Regulatory alignment",
    ],
  },
  {
    id: 4,
    title: "Compliance & Risk Management",
    icon: FaCheckCircle,
    description:
      "Ensure your organization meets industry compliance standards and manages risks.",
    features: [
      "Compliance Assessment",
      "Risk Analysis & Mitigation",
      "Policy Development",
      "Audit Trail Implementation",
      "Training & Awareness Programs",
      "Continuous Monitoring",
    ],
    benefits: [
      "Regulatory compliance",
      "Reduced legal risks",
      "Enhanced reputation",
      "Insurance discounts",
      "Stakeholder confidence",
      "Industry best practices",
    ],
  },
];

const WHY_CHOOSE_US = [
  {
    icon: FaShieldAlt,
    title: "Expert Team",
    description: "Certified security professionals",
  },
  {
    icon: FaAsterisk,
    title: "24/7 Monitoring",
    description: "Round-the-clock threat detection",
  },
  {
    icon: FaCheckCircle,
    title: "Compliance Ready",
    description: "Meet all industry standards",
  },
];

// Subcomponents
const ServiceCard = ({ service }) => {
  const IconComponent = service.icon;

  return (
    <div className="service-detail-card">
      <div className="service-detail-header">
        <IconComponent className="service-detail-icon" aria-hidden="true" />
        <div>
          <h3>{service.title}</h3>
          <p className="service-detail-desc">{service.description}</p>
        </div>
      </div>

      <div className="service-detail-content">
        <div className="detail-section">
          <h4>What We Offer</h4>
          <ul className="features-list">
            {service.features.map((feature, idx) => (
              <li key={idx}>✓ {feature}</li>
            ))}
          </ul>
        </div>

        <div className="detail-section">
          <h4>Key Benefits</h4>
          <ul className="benefits-list">
            {service.benefits.map((benefit, idx) => (
              <li key={idx}>★ {benefit}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const WhyChooseSection = () => (
  <div className="service-intro">
    <h2>Why Choose Our Cybersecurity Services?</h2>
    <p>
      In today's digital landscape, security is not optional—it's essential. Our
      expert team provides comprehensive cybersecurity solutions to protect your
      business, customers, and assets.
    </p>
    <div className="why-choose-grid">
      {WHY_CHOOSE_US.map((item, idx) => {
        const IconComponent = item.icon;
        return (
          <div key={idx} className="why-item">
            <IconComponent className="why-icon" aria-hidden="true" />
            <h3>{item.title}</h3>
            <p>{item.description}</p>
          </div>
        );
      })}
    </div>
  </div>
);

const CTASection = ({ onServiceClick }) => (
  <div className="cta-section">
    <h2>Ready to Secure Your Business?</h2>
    <p>Contact today for a free security consultation</p>
    <button
      className="cta-button"
      onClick={() => onServiceClick && onServiceClick("Cybersecurity")}
    >
      Contact Now
    </button>
  </div>
);

// Main Component
/**
 * Cybersecurity Services Component
 * Displays comprehensive cybersecurity solutions including security audits,
 * penetration testing, data protection, and compliance management.
 *
 * @param {Object} props - Component props
 * @param {Function} props.onServiceClick - Callback when user clicks contact button
 * @returns {React.ReactElement} Cybersecurity service page
 */
function Cybersecurity({ onServiceClick }) {
  return (
    <div className="service-page">
      <div className="service-hero">
        <h1>Cybersecurity Solutions</h1>
        <p>
          Protect your business from cyber threats with comprehensive security
          solutions
        </p>
      </div>

      <div className="service-container">
        <WhyChooseSection />

        <section
          className="services-section"
          aria-labelledby="services-heading"
        >
          <h2 id="services-heading" className="services-heading">
            Our Services
          </h2>

          {CYBERSECURITY_SERVICES.length > 0 ? (
            CYBERSECURITY_SERVICES.map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))
          ) : (
            <p>No services available at this time.</p>
          )}
        </section>

        <CTASection onServiceClick={onServiceClick} />
      </div>
    </div>
  );
}

export default Cybersecurity;
